import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ArrowRight, CheckCircle} from '../../icons';
import {Spinner} from '../../Spinner';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {AlertsApi} from '../../../api/alertsApi';
import {isOk} from '../../../utils/apiResponse';
import {anchorAttr} from '../../../guides/anchors';
import {FILTER_ALL, filterFromParam} from '../../client/panel_v11/alerts/clientAlertsLogic';
import {
    ActiveAlertRow,
    AlertKpis,
    AlertSnoozeModal,
    AlertsToolbar,
    ResolvedAlertRow,
    SnoozedAlertRow,
    copyAlertPrompt,
    useAlertSnooze,
    useRecalculationRefresh,
} from '../shared';
import {PARAM_CLIENT, PARAM_FILTER, PARAM_RULE} from '../alertsCenterPaths';
import {RuleFilterChip} from './RuleFilterChip';
import {ClientFilterChip} from './ClientFilterChip';
import {UncontrolledSummaryCard} from '../uncontrolled/UncontrolledSummaryCard';
import {
    activeItems,
    chipCountFor,
    clientFromParam,
    clientInitials,
    clientsInTray,
    groupsForView,
    groupsWithResolved,
    keyedRows,
    nextResolvedByClient,
    nowSummary,
    ruleFromParam,
    rulesInTray,
    shownItemsCount,
} from './nowLogic';
// Las filas, los KPIs y los chips son los de la pantalla de Alertas del cliente: su CSS cuelga de
// `.ui-v2 .cav-screen`, el contenedor de esta vista.
import '../../client/panel_v11/alerts/ClientAlertsView.scss';

/**
 * «Ahora» — la bandeja de las alertas vigentes de todos los clientes que el usuario ve (Centro de
 * Alertas, F3; mockup A6 de `dashbo-sacudon/estrategia/ux21-alertas.html`).
 *
 * Es la pantalla de Alertas del cliente (`ClientAlertsView`) de todos a la vez, agrupada por cliente y
 * con los clientes más graves primero (el orden lo pone el backend): los mismos ítems, las mismas filas
 * (`alerts_center/shared`), el mismo silenciado (`useAlertSnooze`, que silencia una alerta de varias
 * líneas en todas). Por eso «Vigentes» es la suma de las pills del Listado.
 *
 * El filtro (`?filtro=`), la regla (`?regla=`, lo que arma «Ver qué disparó» de Mis reglas) y el cliente
 * (`?cliente=`, «Por cliente») viven en la URL. Filtran la lista, no los números de arriba (KPIs y la tarjeta
 * de «Inversión sin control»), que son de toda la agencia. `rules` es la lista de Mis reglas que ya cargó la
 * pantalla: de ahí sale el nombre de la regla elegida aunque hoy no tenga alertas.
 */
export function AlertsNowView({rules = []}) {
    const {t} = useTranslation();
    const [data, setData] = useState(null);
    // `null` = sin error; string = la carga falló (se compara `!== null`, patrón de LoadErrorState).
    const [loadError, setLoadError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const rawFilter = searchParams.get(PARAM_FILTER);
    const rawRule = searchParams.get(PARAM_RULE);
    const rawClient = searchParams.get(PARAM_CLIENT);
    const filter = filterFromParam(rawFilter);
    const ruleId = ruleFromParam(rawRule);
    const clientId = clientFromParam(rawClient);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const writeParams = useCallback((nextFilter, nextRule, nextClient, options) => {
        const params = {};
        if (nextFilter !== FILTER_ALL) params[PARAM_FILTER] = nextFilter;
        if (nextRule !== null) params[PARAM_RULE] = nextRule;
        if (nextClient !== null) params[PARAM_CLIENT] = nextClient;
        setSearchParams(params, options);
    }, [setSearchParams]);

    // Un parámetro que no existe abre «Todas» / todas las reglas / todos los clientes, y la URL tiene que
    // decir lo mismo que la pantalla (QA-pt-12 de la pantalla del cliente). `replace`: la corrección no deja
    // historial.
    useEffect(() => {
        const badFilter = rawFilter !== null && filterFromParam(rawFilter) !== rawFilter;
        const badRule = rawRule !== null && ruleFromParam(rawRule) !== rawRule;
        const badClient = rawClient !== null && clientFromParam(rawClient) !== rawClient;
        if (badFilter || badRule || badClient) writeParams(filter, ruleId, clientId, {replace: true});
    }, [rawFilter, rawRule, rawClient, filter, ruleId, clientId, writeParams]);

    // `refresh` = recargar DESPUÉS de silenciar o reactivar, con la lista ya en pantalla: si falla, la
    // lista que se ve sigue siendo válida y se avisa con un toast (mismo criterio que la del cliente).
    // `silent` = el refresco de fondo mientras una línea se recalcula: si falla, calla. Con pedidos cruzados manda
    // el ÚLTIMO que salió: uno que salió antes de «Silenciar» y vuelve después pisaba lo silenciado (review C-08).
    const requestSeqRef = useRef(0);
    const inFlightRef = useRef(0);
    const load = useCallback(({refresh = false, silent = false} = {}) => {
        const seq = ++requestSeqRef.current;
        inFlightRef.current += 1;
        return AlertsApi.getAgencyActiveAlerts().then((response) => {
            if (!mounted.current || seq !== requestSeqRef.current) return;
            if (!isOk(response) || !response.result) {
                if (silent) return;
                if (refresh) {
                    toast.error(t('client_alerts.refresh_error'));
                } else {
                    setLoadError(t('alerts_center.load_error'));
                }
                return;
            }
            setData(response.result);
            setLoadError(null);
        }).finally(() => {
            inFlightRef.current -= 1;
            if (mounted.current) setIsLoading(false);
        });
        // `t` cambia de identidad entre renders de i18next.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const retry = () => {
        setLoadError(null);
        setIsLoading(true);
        load();
    };

    const reload = useCallback(() => load({refresh: true}), [load]);
    const snooze = useAlertSnooze({reload});

    // Las filas que se resolvieron mientras se miraba (card Fnvibexi): quedan en su lugar como «Resuelta». Se
    // comparan los grupos DIBUJADOS antes de esta lectura (`previousGroupsRef`, lo guarda un efecto declarado más
    // abajo, que corre después de éste) con las vigentes que llegan (review C-09).
    const [resolved, setResolved] = useState(() => new Map());
    const previousGroupsRef = useRef([]);
    useEffect(() => {
        if (!data) return;
        const previousGroups = previousGroupsRef.current;
        setResolved((current) => nextResolvedByClient(current, previousGroups, data));
    }, [data]);

    // Mientras una línea se recalcula por un cambio guardado, la bandeja se refresca sola y callada. Con un pedido
    // en vuelo, el turno se saltea (review C-08).
    const allActive = useMemo(() => activeItems(data), [data]);
    const backgroundRefresh = useCallback(() => {
        if (inFlightRef.current > 0) return undefined;
        return load({silent: true});
    }, [load]);
    useRecalculationRefresh(allActive, backgroundRefresh);

    const summary = useMemo(() => nowSummary(data, ruleId, clientId), [data, ruleId, clientId]);
    const groups = useMemo(() => groupsWithResolved(groupsForView(data, filter, ruleId, clientId), resolved,
        filter, ruleId, clientId), [data, filter, ruleId, clientId, resolved]);
    // Lo que quedó dibujado: lo lee la próxima lectura para saber qué se resolvió y en qué lugar estaba.
    useEffect(() => {
        previousGroupsRef.current = groups;
    }, [groups]);
    // Los nombres ACTUALES de las reglas (Mis reglas): mandan sobre el de la alerta, que es una foto del
    // día que disparó (C-ac-fe-ahora-07).
    const currentNames = useMemo(() => Object.fromEntries((rules || [])
        .filter((rule) => rule && rule.name).map((rule) => [String(rule.id), rule.name])), [rules]);
    const trayRules = useMemo(() => rulesInTray(data, currentNames, clientId), [data, currentNames, clientId]);
    const trayClients = useMemo(() => clientsInTray(data, ruleId), [data, ruleId]);
    // El nombre del cliente elegido sale de la bandeja entera: la regla elegida puede dejarlo fuera del menú.
    const clientName = useMemo(() => {
        if (clientId === null || !data || !Array.isArray(data.clients)) return null;
        const client = data.clients.find((item) => String(item.client_id) === clientId);
        return client ? client.client_name : null;
    }, [data, clientId]);
    const snoozeable = useMemo(() => groups.flatMap((group) => group.active)
        .filter((item) => item.snooze && item.snooze.available), [groups]);
    // La guía del Centro apunta a UN «Silenciar»: el primero de la pantalla (mismo orden que las filas).
    const firstSnoozeable = snoozeable.length ? snoozeable[0] : null;

    const ruleName = useMemo(() => {
        if (ruleId === null) return null;
        if (currentNames[ruleId]) return currentNames[ruleId];
        const inTray = trayRules.find((rule) => rule.id === ruleId);
        return inTray ? inTray.name : null;
    }, [ruleId, trayRules, currentNames]);

    // ------------------------------------------------------------------ piezas ------------------

    const activeHint = () => {
        const {kpis} = summary;
        if (!kpis.active || !kpis.clientsTotal) return null;
        return t('alerts_center.now.kpi_active_hint', {count: kpis.clientsTotal, clients: kpis.clientsWithActive});
    };

    const renderGroup = ({client, active, snoozed, rows}) => (
        <section key={client.client_id} className="alc-now__group" data-testid="alc-now-group"
                 aria-label={client.client_name}>
            <header className="alc-now__group-head">
                {/* Avatar y nombre viajan juntos: un nombre largo envuelve al lado del avatar. */}
                <span className="alc-now__who">
                    <span className="alc-now__avatar" aria-hidden="true">{clientInitials(client.client_name)}</span>
                    <span className="alc-now__client">{client.client_name}</span>
                </span>
                <span className="alc-now__counts">
                    {t('alerts_center.now.group_active', {count: client.count})}
                    {client.snoozed_count > 0
                        && ` · ${t('alerts_center.now.group_snoozed', {count: client.snoozed_count})}`}
                </span>
                <Link className="cav-link alc-now__open" to={`/client/${client.client_id}`}>
                    {t('alerts_center.now.open_client')}
                    <ArrowRight size={12} weight="bold"/>
                </Link>
            </header>
            <div className="cav-list">
                {keyedRows(rows.map((row) => row.item)).map(({item, key}, index) => (rows[index].resolved
                    ? <ResolvedAlertRow key={`resolved-${key}`} item={item} phrase={rows[index].resolved.phrase}/>
                    : <ActiveAlertRow key={`active-${key}`} item={item} mailClientId={client.client_id}
                                      onSnooze={(row) => snooze.openSnooze(row)}
                                      onCopyPrompt={(row) => copyAlertPrompt(row, client.client_id, t)}
                                      onReactivate={(row) => snooze.reactivate(row)} isSaving={snooze.isSaving}
                                      snoozeGuideAnchor={item === firstSnoozeable ? anchorAttr('alerts_first_row_snooze') : {}}/>
                ))}
                {/* Con `rows` y no `active`: una «Resuelta» seguida de silenciadas también necesita el separador
                    (review C-10). */}
                {snoozed.length > 0 && rows.length > 0 && (
                    <div className="cav-group">{t('clients_v2.alerts_snoozed_group')}</div>
                )}
                {snoozed.map((item, index) => (
                    <SnoozedAlertRow key={`snoozed-${index}`} item={item} isSaving={snooze.isSaving}
                                     onReactivate={(row) => snooze.reactivate(row)}/>
                ))}
            </div>
        </section>
    );

    const renderEmpty = () => {
        // Con el tope, el chip cuenta TODO (C-ac-fe-ahora-05) y sus filas pueden haber quedado fuera de las
        // más graves que se muestran: se dice eso, no que el filtro no tiene alertas.
        if (data && data.truncated && filter !== FILTER_ALL && chipCountFor(summary.chips, filter) > 0) {
            return (
                <div className="cav-empty" data-testid="alc-now-empty">
                    {t('alerts_center.now.filter_truncated_empty', {n: shownItemsCount(data)})}
                </div>
            );
        }
        // La regla o el cliente tienen alertas, pero ninguna cae en el chip prendido: no es que «no tengan
        // vigentes» (C-ac-fe-ahora-02). Se dice como cualquier filtro sin filas y se ofrece soltar el chip.
        const scopeHasRows = (ruleId !== null || clientId !== null)
            && summary.chips.active + summary.chips.snoozed > 0;
        if (scopeHasRows && filter !== FILTER_ALL) {
            let linkKey = 'alerts_center.now.rule_client_filter_empty_link';
            if (clientId === null) linkKey = 'alerts_center.now.rule_filter_empty_link';
            else if (ruleId === null) linkKey = 'alerts_center.now.client_filter_empty_link';
            return (
                <div className="cav-empty" data-testid="alc-now-empty">
                    {t('client_alerts.empty_filter')}
                    <button type="button" className="cav-row__link"
                            onClick={() => writeParams(FILTER_ALL, ruleId, clientId)}>
                        {t(linkKey)}
                    </button>
                </div>
            );
        }
        // La regla puede tener alertas en otros clientes: se ofrece ver todas las del cliente elegido.
        if (ruleId !== null && clientId !== null) {
            return (
                <div className="cav-empty" data-testid="alc-now-empty">
                    {t('alerts_center.now.rule_client_empty')}
                    <button type="button" className="cav-row__link" onClick={() => writeParams(filter, null, clientId)}>
                        {t('alerts_center.now.client_filter_empty_link')}
                    </button>
                </div>
            );
        }
        if (ruleId !== null) {
            return (
                <div className="cav-empty" data-testid="alc-now-empty">
                    {t('alerts_center.now.rule_empty')}
                    <button type="button" className="cav-row__link" onClick={() => writeParams(filter, null, null)}>
                        {t('alerts_center.now.rule_empty_link')}
                    </button>
                </div>
            );
        }
        if (clientId !== null) {
            return (
                <div className="cav-empty" data-testid="alc-now-empty">
                    {t('alerts_center.now.client_empty')}
                    <button type="button" className="cav-row__link" onClick={() => writeParams(filter, null, null)}>
                        {t('alerts_center.now.client_empty_link')}
                    </button>
                </div>
            );
        }
        // Sin nada vigente es una buena noticia y se dice así; con un filtro prendido es sólo que ese
        // filtro no tiene filas.
        return (
            <div className="cav-empty" data-testid="alc-now-empty">
                {filter === FILTER_ALL && <CheckCircle size={22} weight="bold"/>}
                {filter === FILTER_ALL ? t('alerts_center.now.empty') : t('client_alerts.empty_filter')}
            </div>
        );
    };

    const renderBody = () => {
        if (loadError !== null) {
            return (
                <LoadErrorState
                    title={loadError}
                    text={t('alerts_center.now.load_error_explanation')}
                    retryLabel={t('client_alerts.load_error_retry')}
                    onRetry={retry}
                />
            );
        }
        if (isLoading && !data) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        return (
            <>
                {/* La plata que ninguna línea mira no es una alerta de una línea: sin esto, «Ahora» dice
                    «Todo bien» con plata sin control. Si el pedido falla, no se dibuja. */}
                <UncontrolledSummaryCard className="-now"/>
                <AlertKpis summary={summary.kpis} activeHint={activeHint()} guideAnchor={anchorAttr('alerts_kpis')}/>
                <AlertsToolbar filter={filter} summary={summary.chips}
                               onChangeFilter={(next) => writeParams(next, ruleId, clientId)}
                               snoozeable={snoozeable} countInLabel
                               onSnoozeAll={(rows) => snooze.openSnooze(null, rows)}>
                    {/* Primero el cliente: la bandeja está agrupada por cliente. */}
                    <ClientFilterChip clients={trayClients} clientId={clientId} clientName={clientName}
                                      onChange={(next) => writeParams(filter, ruleId, next)}/>
                    <RuleFilterChip rules={trayRules} ruleId={ruleId} ruleName={ruleName}
                                    onChange={(next) => writeParams(filter, next, clientId)}/>
                </AlertsToolbar>
                {data && data.truncated && (
                    <p className="alc-now__note" data-testid="alc-now-truncated">
                        {t('alerts_center.now.truncated', {n: shownItemsCount(data)})}
                    </p>
                )}
                {groups.length ? <div className="alc-now__groups">{groups.map(renderGroup)}</div> : renderEmpty()}
                <div className="cav-foot">
                    <span>{t('client_alerts.foot')}</span>
                </div>
            </>
        );
    };

    return (
        <div className="cav-screen alc-now" data-testid="alc-now">
            {renderBody()}
            <AlertSnoozeModal snooze={snooze}/>
        </div>
    );
}

export default AlertsNowView;
